import { formatCaseNumber } from "@/api/cases/caseNumber";
import { CaseRelationsApi } from "@/api/cases/CaseRelationsApi";
import {
  useCaseRelationCapabilities,
  useCaseRelations,
  useMyCases,
  useSameIdentityCases,
} from "@/api/cases/useCases";
import { queryKeys } from "@/api/queryKeys";
import { routes } from "@/constants/routes";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { FloatingLabelListPicker } from "@/ui/components/FloatingLabelListPicker";
import { HeaderAction } from "@/ui/components/HeaderAction";
import { SoftSection } from "@/ui/components/SoftSection";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { Text, View } from "react-native";

type Mode =
  | null
  | "mother"
  | "sibling"
  | "suspect"
  | "confirm"
  | "undo";

const DIRECTION_LABEL: Record<string, string> = {
  mother_of: "Mutter von",
  child_of: "Kind von",
  sibling: "Geschwister",
  identity_suspected: "Identität vermutet",
  identity_confirmed: "Identität bestätigt",
};

export function CaseRelationsSection({
  caseId,
  organizationId,
}: {
  caseId: string;
  organizationId: string;
}) {
  const { theme } = useTheme();
  const router = useRouter();
  const qc = useQueryClient();
  const { relations, loading, refetch } = useCaseRelations(caseId);
  const { cases: sameIdentity } = useSameIdentityCases(caseId);
  const { cases: allCases } = useMyCases();
  const { canDocumentSuspicion, isOrgAdmin } =
    useCaseRelationCapabilities(organizationId);

  const [mode, setMode] = useState<Mode>(null);
  const [otherCaseId, setOtherCaseId] = useState<string | null>(null);
  const [role, setRole] = useState<"mother" | "child">("mother");
  const [canonicalCaseId, setCanonicalCaseId] = useState<string | null>(null);
  const [undoRelationId, setUndoRelationId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const otherOptions = useMemo(
    () =>
      allCases
        .filter((c) => c.id !== caseId)
        .map((c) => ({
          value: c.id,
          label: `${formatCaseNumber(c.case_number)}${
            c.name ? ` · ${c.name}` : ""
          }`,
        })),
    [allCases, caseId]
  );

  const invalidate = async () => {
    await Promise.all([
      refetch(),
      qc.invalidateQueries({ queryKey: queryKeys.cases.sameIdentity(caseId) }),
      qc.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) }),
      qc.invalidateQueries({ queryKey: queryKeys.cases.my }),
    ]);
  };

  const resetForm = () => {
    setMode(null);
    setOtherCaseId(null);
    setCanonicalCaseId(null);
    setUndoRelationId(null);
    setNote("");
    setReason("");
    setMessage(null);
  };

  const onSave = async () => {
    setMessage(null);
    setBusy(true);
    try {
      if (mode === "mother") {
        if (!otherCaseId) throw new Error("Bitte einen Fall wählen.");
        await CaseRelationsApi.createMotherOf(
          role === "mother"
            ? { motherCaseId: caseId, childCaseId: otherCaseId, note }
            : { motherCaseId: otherCaseId, childCaseId: caseId, note }
        );
      } else if (mode === "sibling") {
        if (!otherCaseId) throw new Error("Bitte einen Fall wählen.");
        await CaseRelationsApi.linkAsSiblings([caseId, otherCaseId], note);
      } else if (mode === "suspect") {
        if (!otherCaseId) throw new Error("Bitte einen Fall wählen.");
        await CaseRelationsApi.createIdentitySuspicion({
          caseAId: caseId,
          caseBId: otherCaseId,
          note,
        });
      } else if (mode === "confirm") {
        if (!otherCaseId || !canonicalCaseId) {
          throw new Error("Fall und kanonische Akte wählen.");
        }
        if (!reason.trim()) throw new Error("Begründung erforderlich.");
        await CaseRelationsApi.confirmSameIdentity({
          caseAId: caseId,
          caseBId: otherCaseId,
          canonicalCaseId,
          reason: reason.trim(),
        });
      } else if (mode === "undo") {
        if (!undoRelationId) throw new Error("Relation wählen.");
        if (!reason.trim()) throw new Error("Begründung erforderlich.");
        await CaseRelationsApi.undoSameIdentityMerge(
          undoRelationId,
          reason.trim()
        );
      }
      await invalidate();
      resetForm();
      setMessage("Gespeichert.");
    } catch (e: any) {
      setMessage(e?.message ?? "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const confirmedRelations = relations.filter(
    (r) => r.direction === "identity_confirmed"
  );

  return (
    <SoftSection
      title="Beziehungen"
      headerRight={
        !mode ? (
          <HeaderAction
            label="Hinzufügen"
            onPress={() => {
              setMessage(null);
              setMode("sibling");
            }}
          />
        ) : undefined
      }
    >
      <View style={{ gap: theme.space.sm }}>
        {loading ? (
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
            Laden…
          </Text>
        ) : relations.length === 0 && sameIdentity.length === 0 ? (
          <EmptyState
            title="Noch keine Beziehungen."
            hint="Mutter–Kind, Geschwister oder Identität über die Aktionen unten ergänzen."
          />
        ) : (
          <View>
            {relations.map((r) => (
              <DenseRow
                key={`${r.id}-${r.related_case_id}-${r.direction}`}
                title={`${DIRECTION_LABEL[r.direction] ?? "Beziehung"}: ${formatCaseNumber(
                  r.related_case_number
                )}${r.related_case_name ? ` · ${r.related_case_name}` : ""}`}
                meta={r.note ?? undefined}
                onPress={() =>
                  router.push(routes.myAnimal(r.related_case_id) as any)
                }
              />
            ))}
            {sameIdentity.map((c) => (
              <DenseRow
                key={`same-${c.id}`}
                title={`Gleicher Identität: ${formatCaseNumber(c.case_number)}${
                  c.name ? ` · ${c.name}` : ""
                }`}
                meta="Über Identity-Merge"
                onPress={() => router.push(routes.myAnimal(c.id) as any)}
              />
            ))}
          </View>
        )}

        {!mode ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: theme.space.md,
              paddingTop: theme.space.xs,
            }}
          >
            <HeaderAction label="Mutter–Kind" onPress={() => setMode("mother")} />
            <HeaderAction label="Geschwister" onPress={() => setMode("sibling")} />
            {canDocumentSuspicion ? (
              <HeaderAction
                label="Identität vermuten"
                onPress={() => setMode("suspect")}
              />
            ) : null}
            {isOrgAdmin ? (
              <>
                <HeaderAction
                  label="Identität bestätigen"
                  onPress={() => setMode("confirm")}
                />
                {confirmedRelations.length > 0 ? (
                  <HeaderAction
                    label="Merge rückgängig"
                    tone="danger"
                    onPress={() => {
                      setUndoRelationId(confirmedRelations[0].id);
                      setMode("undo");
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </View>
        ) : (
          <View style={{ gap: theme.space.sm }}>
            {mode === "mother" ? (
              <>
                <FloatingLabelListPicker
                  label="Diese Akte ist"
                  options={[
                    { value: "mother", label: "Mutter" },
                    { value: "child", label: "Kind" },
                  ]}
                  value={role}
                  onChange={(v) =>
                    setRole(v === "child" ? "child" : "mother")
                  }
                />
                <FloatingLabelListPicker
                  label="Anderer Fall"
                  options={otherOptions}
                  value={otherCaseId}
                  onChange={setOtherCaseId}
                  placeholder="Fall wählen…"
                />
              </>
            ) : null}
            {mode === "sibling" || mode === "suspect" ? (
              <FloatingLabelListPicker
                label="Anderer Fall"
                options={otherOptions}
                value={otherCaseId}
                onChange={setOtherCaseId}
                placeholder="Fall wählen…"
              />
            ) : null}
            {mode === "confirm" ? (
              <>
                <FloatingLabelListPicker
                  label="Zweiter Fall"
                  options={otherOptions}
                  value={otherCaseId}
                  onChange={(v) => {
                    setOtherCaseId(v);
                    setCanonicalCaseId(caseId);
                  }}
                  placeholder="Fall wählen…"
                />
                <FloatingLabelListPicker
                  label="Kanonische Akte behalten"
                  options={[
                    {
                      value: caseId,
                      label: "Diese Akte",
                    },
                    ...(otherCaseId
                      ? otherOptions.filter((o) => o.value === otherCaseId)
                      : []),
                  ]}
                  value={canonicalCaseId}
                  onChange={setCanonicalCaseId}
                />
                <FloatingLabelInput
                  label="Begründung (Pflicht)"
                  value={reason}
                  onChangeText={setReason}
                />
              </>
            ) : null}
            {mode === "undo" ? (
              <FloatingLabelInput
                label="Begründung für Undo (Pflicht)"
                value={reason}
                onChangeText={setReason}
              />
            ) : null}
            {mode !== "confirm" && mode !== "undo" ? (
              <FloatingLabelInput
                label="Notiz (optional)"
                value={note}
                onChangeText={setNote}
              />
            ) : null}
            <View
              style={{
                flexDirection: "row",
                gap: theme.space.md,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <HeaderAction label="Abbrechen" onPress={resetForm} />
              <AppButton onPress={onSave} disabled={busy}>
                {busy ? "Speichern…" : "Speichern"}
              </AppButton>
            </View>
          </View>
        )}

        {message ? (
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
            {message}
          </Text>
        ) : null}
      </View>
    </SoftSection>
  );
}
